import { useEffect, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { type Locale } from '@realadvisor/agency-utils';
import { Image } from '@realadvisor/image';
import { Home, Info, Pencil, Share2 } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { PropertyImageUploadModal } from '@modules/_shared/components/image-upload/implementations/PropertyImageUploadModal';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  formatAddress,
  formatDateNumeric,
} from '@modules/_shared/utils/formatting';
import { ReviewInvitationModal } from '@modules/visibility-reviews/components/ReviewInvitationModal';
import {
  CardButton,
  CardItem,
  MemberDetailsCard,
} from '@modules/visibility-team/MemberDetailsCard';
import { ShareDialog } from '@modules/visibility-team/ShareDialog';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { customPalette } from '@shared/theme/StyleProvider';
import { formatPrice } from '@shared/utils/format-price';

import { VerificationStatus } from './VerificationStatus';

export const GET_TRANSACTION_BY_ID = gql(/* GraphQL */ `
  query GetTransactionById($transactionId: uuid!) {
    property_transactions_by_pk(id: $transactionId) {
      id
      property_id
      signature_date
      purchase_price
      property {
        id
        country_code
        property_type {
          label
        }
        number_of_rooms
        living_surface
        built_surface
        route
        street_number
        postcode
        locality_place {
          label
        }
        property_images(limit: 1, order_by: { order_nr: asc }) {
          image {
            url
          }
        }
      }
      reviews_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
        }
      }
      reviews(where: { completed: { _eq: true } }) {
        id
        is_buyer
      }
      seller_broker {
        id
        first_name
        last_name
        agent_slug
      }
      seller_broker_team {
        id
        name
      }
    }
  }
`);

// Type for transaction property
type TransactionProperty = {
  id: string;
  country_code?: string | null;
  property_type?: { label?: string | null } | null;
  number_of_rooms?: number | null;
  living_surface?: number | null;
  built_surface?: number | null;
  route?: string | null;
  street_number?: string | null;
  postcode?: string | null;
  locality_place?: { label?: string | null } | null;
  property_images?: Array<{
    image?: { url?: string | null } | null;
  }> | null;
};

const PropertyImageWithBadge = ({
  imageUrl,
  onEditImage,
}: {
  imageUrl?: string | null;
  onEditImage: () => void;
}) => {
  const { t } = useLocale();

  return (
    <Box sx={{ position: 'relative', mb: 3 }}>
      <Box
        sx={{
          height: 200,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {imageUrl ? (
          <>
            <Image src={imageUrl} options={{ w: 600, h: 200 }} />
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                backgroundColor: 'primary.main',
                color: '#fff',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {t('SOLD')}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Home size={120} style={{ color: customPalette.gray400 }} />
          </Box>
        )}

        {!imageUrl && (
          <IconButton
            onClick={onEditImage}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'primary.main',
              transition: 'background-color 0.25s',
              '&:hover': {
                backgroundColor: customPalette.white,
              },
            }}
            size="small"
          >
            <Pencil size={18} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const getPropertyDetails = (
  property: TransactionProperty | null,
  isEsTenant: boolean,
  t: Translate,
) => {
  if (!property) {
    return null;
  }

  const rooms = property.number_of_rooms;
  const surface = isEsTenant ? property.built_surface : property.living_surface;
  const type = property.property_type?.label;
  const address = formatAddress(property, ', ', true);

  if (!type && !rooms && !surface && !address) {
    return null;
  }

  return {
    type: type ?? null,
    roomsAndSurface:
      rooms || surface
        ? [
            rooms ? t('{{count}} rooms', { count: rooms }) : null,
            surface ? `${surface} m²` : null,
          ]
            .filter(Boolean)
            .join(' • ')
        : null,
    address: address || null,
  };
};

export type TransactionDetailsModalProps = {
  transactionId: string;
  onClose: () => void;
};

export const TransactionDetailsModal = ({
  transactionId,
  onClose,
}: TransactionDetailsModalProps) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  useEffect(() => {
    setDialogOpen(true);
  }, []);

  const { data, loading, error } = useQuery(GET_TRANSACTION_BY_ID, {
    variables: { transactionId },
    skip: !transactionId,
  });

  useEffect(() => {
    if (error) {
      setErrorLike(error);
    }
  }, [error]);

  const selectedTransaction = data?.property_transactions_by_pk;

  const propertyId = selectedTransaction?.property_id ?? '';

  const isEsTenant = me?.tenant.country_code === 'CH';
  const currency = getCurrencyByCountryCode(
    selectedTransaction?.property?.country_code ?? 'CH',
  );

  const hasSellerReview =
    selectedTransaction?.reviews?.some(r => !r.is_buyer) ?? false;
  const hasBuyerReview =
    selectedTransaction?.reviews?.some(r => r.is_buyer) ?? false;

  const property = selectedTransaction?.property;
  const imageUrl = property?.property_images?.[0]?.image?.url;
  const propertyDetails = property
    ? getPropertyDetails(property, isEsTenant, t)
    : null;

  const handleClose = () => {
    setDialogOpen(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  if (loading) {
    return (
      <SlideDialog
        open={dialogOpen}
        onClose={handleClose}
        title={t('Transaction')}
        cancelText={t('Close')}
      >
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <LoadingSpinner />
        </Box>
      </SlideDialog>
    );
  }

  if (error || selectedTransaction == null) {
    return (
      <>
        <SlideDialog
          open={dialogOpen}
          onClose={handleClose}
          title={t('Transaction')}
          cancelText={t('Close')}
        >
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">
              {t('Failed to load transaction details')}
            </Typography>
          </Box>
        </SlideDialog>

        <MutationErrorSnackbar
          error={apolloError}
          onClose={() => setErrorLike(null)}
        />
      </>
    );
  }

  return (
    <>
      <SlideDialog
        open={dialogOpen}
        onClose={handleClose}
        title={t('Transaction')}
        cancelText={t('Close')}
      >
        <Box sx={{ p: 2 }}>
          <>
            <PropertyImageWithBadge
              imageUrl={imageUrl}
              onEditImage={
                propertyId ? () => setImageUploadOpen(true) : () => {}
              }
            />

            <Stack
              sx={{
                gap: 2,
              }}
            >
              {/* Reviews Section */}
              <Box>
                <MemberDetailsCard title={t('Reviews')}>
                  <CardItem xs={12} sm={12}>
                    <Stack spacing={1} direction="row">
                      <VerificationStatus
                        isVerified={hasSellerReview}
                        label={t('Seller review')}
                      />
                      <VerificationStatus
                        isVerified={hasBuyerReview}
                        label={t('Buyer review')}
                      />
                    </Stack>

                    {!hasSellerReview && !hasBuyerReview && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          mt: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Info size={16} style={{ marginRight: 8 }} />
                        {t(
                          'Get a review from the seller and / or the buyer to verify this transaction',
                        )}
                      </Typography>
                    )}
                  </CardItem>

                  <CardButton onClick={() => setOpenReviewModal(true)}>
                    {t('Get reviews')}
                  </CardButton>
                </MemberDetailsCard>
              </Box>
              {/* Seller Agent Section */}
              <Box>
                <MemberDetailsCard title={t('Seller agent')}>
                  <CardItem xs={12} sm={12}>
                    <Typography variant="body1">
                      {[
                        selectedTransaction.seller_broker?.first_name,
                        selectedTransaction.seller_broker?.last_name,
                      ]
                        .filter(Boolean)
                        .join(' ') || '-'}
                    </Typography>
                    {selectedTransaction.seller_broker_team?.name && (
                      <Typography variant="body1">
                        {selectedTransaction.seller_broker_team.name}
                      </Typography>
                    )}
                  </CardItem>
                </MemberDetailsCard>
              </Box>
              {/* Property Section */}
              {propertyDetails && (
                <Box>
                  <MemberDetailsCard title={t('Property')}>
                    <CardItem xs={12} sm={12}>
                      {propertyDetails?.type && (
                        <Typography variant="body1">
                          {propertyDetails.type}
                        </Typography>
                      )}
                      {propertyDetails?.roomsAndSurface && (
                        <Typography variant="body1">
                          {propertyDetails.roomsAndSurface}
                        </Typography>
                      )}
                      {propertyDetails?.address && (
                        <Typography variant="body1">
                          {propertyDetails.address}
                        </Typography>
                      )}
                    </CardItem>
                  </MemberDetailsCard>
                </Box>
              )}
              {/* Transaction Section */}
              <Box>
                <MemberDetailsCard title={t('Transaction')}>
                  <CardItem xs={12} sm={12}>
                    <Typography variant="body1">
                      {selectedTransaction.signature_date
                        ? formatDateNumeric(
                            new Date(selectedTransaction.signature_date),
                            locale,
                          )
                        : '-'}
                    </Typography>
                    <Typography variant="body1">
                      {formatPrice(
                        selectedTransaction.purchase_price,
                        locale,
                        currency,
                      )}
                    </Typography>
                  </CardItem>
                </MemberDetailsCard>
              </Box>
              {selectedTransaction?.seller_broker?.agent_slug && (
                <CardButton
                  xs={12}
                  variant="outlined"
                  fullWidth
                  endIcon={<Share2 size={18} />}
                  onClick={() => setShareDialogOpen(true)}
                >
                  {t('Share transaction')}
                </CardButton>
              )}
            </Stack>
          </>
        </Box>
      </SlideDialog>
      <ReviewInvitationModal
        open={openReviewModal}
        onClose={() => setOpenReviewModal(false)}
        initialAgentId={selectedTransaction?.seller_broker?.id}
      />
      {propertyId && (
        <PropertyImageUploadModal
          propertyId={propertyId}
          open={imageUploadOpen}
          onClose={() => setImageUploadOpen(false)}
        />
      )}
      {selectedTransaction?.seller_broker?.agent_slug && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          shareType="transaction"
          agentSlug={selectedTransaction.seller_broker.agent_slug}
          transactionId={transactionId}
          tenantId={me?.tenant?.id ?? ''}
          locale={locale.toLowerCase() as Locale}
        />
      )}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
